import React, { useState } from 'react';
import moment from 'moment';
import OrderSummary from '../../dashboard/components/order-summary';
import PassbookService from '../../../services/passbook.service';
import Loader from '../../../shared/common/loader';
import TableLoader from '../../../shared/common/table-loader';
import { formatAmount, getTransactionName } from '../../../utils';
import { userStore } from 'store/UserStore';
import '../table.css';

interface PropsType {
  walletData: any;
  tableLoading?: boolean;
}

const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  APPROVED: { bg: '#E6F4EC', color: '#1A6637' },
  PENDING: { bg: '#FEF6E0', color: '#7A5010' },
  REJECTED: { bg: '#FDEAEA', color: '#961B1B' },
  'APPROVAL PENDING': { bg: '#E6EEFF', color: '#1E3A8A' },
  'VERIFICATION PENDING': { bg: '#F1F2F4', color: '#5C6470' },
};

const getStatusStyle = (status: string) =>
  STATUS_COLORS[status] ?? { bg: '#F1F2F4', color: '#5C6470' };

const StatusBadge = ({ status }: { status: string }) => {
  const sc = getStatusStyle(status);
  return (
    <span
      className='txn-status-badge'
      style={{ background: sc.bg, color: sc.color }}
    >
      {status}
    </span>
  );
};

const TypeBadge = ({ type }: { type: string }) => {
  if (type === 'ORDER_SELL')
    return <span className='txn-type-badge buy'>BUY</span>;
  if (type === 'ORDER_BUYBACK')
    return <span className='txn-type-badge sell'>SELL</span>;
  if (type === 'CASHBACK')
    return <span className='txn-type-badge gift'>CASHBACK</span>;
  return <span className='txn-type-badge'>{type}</span>;
};

const WalletTransactions: React.FC<PropsType> = ({ walletData, tableLoading = false }) => {
  const [customer, user] = userStore((state: any) => [
    state.customer,
    state.user,
  ]);
  const [selectedWalletData, setSelectedWalletData] = useState<any>(null);
  const [isShow, setIsShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const onHandleSideModal = (value: boolean) => setIsShow(value);

  const getOrderDetails = async (orderRef: string) => {
    try {
      setLoading(true);
      const res: any = await PassbookService.getOrderDetails(
        orderRef,
        user?.userId
      );
      if (res.status === 200 && res.data.data.result?.length > 0) {
        setSelectedWalletData(res.data.data.result[0]);
        onHandleSideModal(true);
      }
    } catch (e: any) {
      console.log(e.message);
    } finally {
      setLoading(false);
    }
  };

  const getAmountPrefix = (record: any): string => {
    if (record.type === 'ORDER_SELL' && record.method === 'WALLET') return '-';
    if (record.type === 'ORDER_SELL' && ['UPI', 'NEBK'].includes(record.method)) return '+';
    if (
      (record.type === 'WALLET' && ['UPI', 'NEBK'].includes(record.method)) ||
      (record.type === 'CASHBACK' && record.method === 'WALLET')
    ) return '+';
    if (record.type === 'ORDER_BUYBACK') return '+';
    if (record.type === 'WALLET' && record.method === 'NEFT') return '-';
    return '';
  };

  const mapStatus = (status: string): string => {
    switch (status) {
      case 'ACCEPTED': return 'APPROVED';
      case 'REJECTED': return 'REJECTED';
      case 'TRANSACTION_APPROVAL_PENDING': return 'APPROVAL PENDING';
      case 'TRANSACTION_VERIFICATION_PENDING': return 'VERIFICATION PENDING';
      default: return 'PENDING';
    }
  };

  const isViewable = (type: string) =>
    type === 'ORDER_SELL' || type === 'ORDER_BUYBACK';

  return (
    <>
      {selectedWalletData && (
        <OrderSummary
          handleModal={onHandleSideModal}
          isShow={isShow}
          data={selectedWalletData}
          isPassbookRedirect={false}
          displaySummary={true}
          displayInvoice={
            customer?.customerCategory !== 'REGISTERED' ||
            selectedWalletData?.type !== 'BUYBACK'
          }
        />
      )}
      {loading && <Loader />}

      <div className='txn-wrapper'>
        {tableLoading && <TableLoader />}
        <div className='txn-scroll'>
          <table className='txn-table'>
            <colgroup>
              <col className='txn-col-orderid' />
              <col className='txn-col-type' />
              <col className='txn-col-order' />
              <col className='txn-col-date' />
              <col className='txn-col-status' />
              <col className='txn-col-amount' />
              <col className='txn-col-action' />
            </colgroup>
            <thead>
              <tr>
                <th>Transaction ID</th>
                <th>Type</th>
                <th>Transaction</th>
                <th>Date</th>
                <th>Status</th>
                <th className='right'>Amount</th>
                <th className='center'>Action</th>
              </tr>
            </thead>
            <tbody>
              {walletData.length === 0 && (
                <tr>
                  <td colSpan={7} className='txn-empty'>
                    <div className='txn-empty-icon'></div>
                    <div className='txn-empty-text'>
                      No wallet transactions available
                    </div>
                  </td>
                </tr>
              )}
              {walletData.map((transaction: any, index: number) => {
                const prefix = getAmountPrefix(transaction);
                const mappedStatus = mapStatus(transaction.status);
                return (
                 <tr key={`${transaction.transactionMasterId}-${index}`} className='txn-row-single'>
                    {/* Transaction ID */}
                    <td>
                      <span className='txn-order-id'>
                        {transaction.transactionMasterId}
                      </span>
                    </td>

                    {/* Type */}
                    <td>
                      <TypeBadge type={transaction.type} />
                    </td>

                    {/* Transaction name */}
                    <td>
                      <div className='txn-order-cell'>
                        <div className='txn-order-line'>
                          <span className='txn-metal-name'>
                            {getTransactionName(transaction)}
                          </span>
                        </div>
                      </div>
                    </td>

                    {/* Date */}
                    <td>
                      <div className='txn-date'>
                        {moment(
                          transaction.date,
                          'DD-MM-YYYY hh:mm:ss'
                        ).format('DD MMM YYYY')}
                      </div>
                      <div className='txn-time'>
                        {moment(
                          transaction.date,
                          'DD-MM-YYYY hh:mm:ss'
                        ).format('hh:mm A')}
                      </div>
                    </td>

                    {/* Status */}
                    <td>
                      <StatusBadge status={mappedStatus} />
                    </td>

                    {/* Amount */}
                    <td className='right'>
                      <span className='txn-amount-parent'>
                        ₹ {prefix} {formatAmount(transaction.amount)}
                      </span>
                    </td>

                    {/* Action */}
                    <td className='center'>
                      {isViewable(transaction.type) && (
                        <button
                          className='txn-view-btn'
                          onClick={() => getOrderDetails(transaction.extRefId)}
                        >
                          <svg
                            width='12'
                            height='12'
                            viewBox='0 0 24 24'
                            fill='none'
                          >
                            <path
                              d='M8 7V3M16 7V3M3 11H21M5 5H19C20.1 5 21 5.9 21 7V19C21 20.1 20.1 21 19 21H5C3.9 21 3 20.1 3 19V7C3 5.9 3.9 5 5 5Z'
                              stroke='currentColor'
                              strokeWidth='2'
                              strokeLinecap='round'
                              strokeLinejoin='round'
                            />
                          </svg>
                          View
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default WalletTransactions;